// src/pages/orders/OrdersFbrDbr.jsx
import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown } from "react-bootstrap";
import { RotateLoader } from "react-spinners";
import { BsArrowBarLeft, BsArrowBarRight } from "react-icons/bs";

import { AuthContext } from "../../context/main/auth-context";
import "../../css/orders/orders.css";

import OrdersHeader from "../../components/orders/orders-header";
import OrderListItemsBolShipStep3 from "../../components/bol/order-list-item-bol-ship-step-3";
import AssistantToggleButton from "../../components/main/assistant-toggle-button";
import { PDFDocument } from "pdf-lib";

import {
  DymoPrintersList,
  printLabels,
} from "../../components/dymo/printHandler";

/**
 * Helpers
 */
const safeJSONParse = (val, fallback = null) => {
  try {
    if (typeof val !== "string") return fallback;
    return JSON.parse(val);
  } catch {
    return fallback;
  }
};

const uint8ArrayToBase64 = (bytes) => {
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const base64ToArrayBuffer = (data) => {
  const binaryString = atob(data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
  return bytes;
};

const openPdfBlobInNewTab = (blob) => {
  const url = URL.createObjectURL(blob);
  const w = window.open(url);
  // Best effort cleanup
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return w;
};

const printPdfBlob = async (blob) => {
  const url = URL.createObjectURL(blob);

  // 1) Probeer in nieuw tabblad met native print
  const win = window.open(url);
  if (win) {
    win.onload = () => {
      try {
        win.focus();
        win.print();
      } catch {}
    };
    // Cleanup
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }

  // 2) Fallback: verborgen iframe
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = url;

  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch {}
    setTimeout(() => {
      URL.revokeObjectURL(url);
      document.body.removeChild(iframe);
    }, 10_000);
  };

  document.body.appendChild(iframe);
};

const extractLabelsFromResponse = (labelsPDF) => {
  // Verwacht: labelsPDF.ResponseShipments[].Labels[].Content (base64)
  if (!labelsPDF || !Array.isArray(labelsPDF.ResponseShipments)) return [];
  const out = [];
  for (const shipment of labelsPDF.ResponseShipments) {
    if (!shipment || !Array.isArray(shipment.Labels)) continue;
    for (const label of shipment.Labels) {
      if (label?.Content) out.push(label.Content);
    }
  }
  return out;
};

const mergeBase64Pdfs = async (base64Pdfs = []) => {
  if (!base64Pdfs.length) return null;

  const merged = await PDFDocument.create();
  for (const b64 of base64Pdfs) {
    const pdfBytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const pdfDoc = await PDFDocument.load(pdfBytes);
    const pages = await merged.copyPages(pdfDoc, pdfDoc.getPageIndices());
    pages.forEach((p) => merged.addPage(p));
  }
  const mergedBytes = await merged.save();
  return new Blob([mergedBytes], { type: "application/pdf" });
};

const OrdersFbrDbr = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [loadingState, setLoadingState] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [checkedOrders, setCheckedOrders] = useState([]);
  const [labelsPDF, setLabelsPDF] = useState(null);
  const [orderActionsAvailable, setOrderActionsAvailable] = useState(false);

  // DYMO printing (optioneel)
  const printers = DymoPrintersList();
  const [currentPrinter, setCurrentPrinter] = useState(null);
  const [twinTurboRoll, setTwinTurboRoll] = useState("Left");
  const [currentLabel, setCurrentLabel] = useState("label99010");

  // Init laden uit sessionStorage
  useEffect(() => {
    (async () => {
      try {
        setLoadingState("Verzendoverzicht wordt geladen…");

        const labelsPDFProp = safeJSONParse(sessionStorage.getItem("labelsPDF"));
        const checkedOrdersProp = safeJSONParse(sessionStorage.getItem("checkedOrders"), []);
        const shippingMethodProp = safeJSONParse(sessionStorage.getItem("shippingMethod"));

        setLabelsPDF(labelsPDFProp || null);
        setCheckedOrders(Array.isArray(checkedOrdersProp) ? checkedOrdersProp : []);

        if (Array.isArray(checkedOrdersProp) && checkedOrdersProp.length > 0 && shippingMethodProp) {
          setOrderActionsAvailable(true);
        } else {
          setOrderActionsAvailable(false);
        }

        setLoadingState(false);
      } catch (e) {
        setErrorMessage("Er ging iets mis bij het laden van het verzendoverzicht.");
        setLoadingState(false);
      }
    })();
  }, []);

  // Printer selectie
  useEffect(() => {
    if (!printers || printers.length === 0) return;
    const twin = printers.find((p) => p.modelName === "DYMO LabelWriter 450 Twin Turbo");
    setCurrentPrinter(twin || printers[0]);
    console.log(printers)
  }, [printers]);

  const handlePrinterChoice = (printer) => setCurrentPrinter(printer);
  const handleTwinTurboRollChoice = (roll) => setTwinTurboRoll(roll);
  const handleLabelChoice = (label) => setCurrentLabel(label === "label11352" ? "label11352" : "label99010");

  const labelBase64List = useMemo(() => extractLabelsFromResponse(labelsPDF), [labelsPDF]);

  const handleViewLabels = useCallback(async () => {
    if (!labelBase64List.length) return;
    try {
      setLoadingState("Labels samenvoegen…");
      const blob = await mergeBase64Pdfs(labelBase64List);
      setLoadingState(false);
      if (!blob) return;
      openPdfBlobInNewTab(blob);
    } catch (e) {
      setLoadingState(false);
      setErrorMessage("Kon labels niet samenvoegen/bekijken.");
    }
  }, [labelBase64List]);

  const handlePrint = useCallback(async () => {
    if (!labelBase64List.length) return;
    try {
      setLoadingState("Labels voorbereiden voor print…");
      const blob = await mergeBase64Pdfs(labelBase64List);
      setLoadingState(false);
      if (!blob) return;
      await printPdfBlob(blob);
    } catch (e) {
      setLoadingState(false);
      setErrorMessage("Printen mislukt (samenvoegen/print).");
    }
  }, [labelBase64List]);

  return (
    <React.Fragment>
      {/* Optioneel: eigen header component */}
      <OrdersHeader title="Bestellingen verzonden" />

      <div className="title-header title-header-ship-orders">
        <span>Bestellingen verzonden</span>
        <div className="shipmentSteps">
          <div className="shipmentOneStep shipmentFirstStep finished">
            <div className="shipmentOneStepCircle">1</div>
            <div>Verzendmethode</div>
          </div>
          <div className="shipmentStepsLine" />
          <div className="shipmentOneStep finished">
            <div className="shipmentOneStepCircle">2</div>
            <div>Overzicht</div>
          </div>
          <div className="shipmentStepsLine" />
          <div className="shipmentOneStep finished">
            <div className="shipmentOneStepCircle">3</div>
            <div>Bevestiging</div>
          </div>
        </div>
      </div>

      {orderActionsAvailable && (
        <div className="orders-container-header ship-orders-container-header">
          <div className="orders-container-header-actions ship-orders-container-header-actions shipped-orders-container-header-actions">
            <div className="labelActionsContainer">

              {/* DYMO UI (optioneel): laat staan voor later gebruik */}
              {/* <div className="confirmedOrderPrintOptions">
                <Dropdown>
                  <Dropdown.Toggle
                    className={!currentPrinter ? "disabled" : ""}
                    variant="secondary"
                    id="printer-dropdown"
                  >
                    {currentPrinter?.name || "Geen verbinding met printer"}
                  </Dropdown.Toggle>
                  {currentPrinter && (
                    <Dropdown.Menu>
                      {printers.map((printer) => (
                        <Dropdown.Item
                          key={printer.modelName}
                          onClick={() => handlePrinterChoice(printer)}
                        >
                          {printer.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  )}
                </Dropdown>

                {currentPrinter?.isTwinTurbo && (
                  <Dropdown>
                    <Dropdown.Toggle variant="secondary">
                      {twinTurboRoll === "Left" ? (<><BsArrowBarLeft /> Links</>) : (<>Rechts <BsArrowBarRight /></>)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleTwinTurboRollChoice("Left")}>
                        <BsArrowBarLeft /> Links
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleTwinTurboRollChoice("Right")}>
                        Rechts <BsArrowBarRight />
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}

                <Dropdown>
                  <Dropdown.Toggle variant="secondary">
                    {currentLabel}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleLabelChoice("label99010")}>
                      99010 - 89x28mm
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleLabelChoice("label11352")}>
                      11352 - 54x25mm
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>

                <Button
                  disabled={!currentPrinter || !checkedOrders?.length}
                  className="printLabelsButton"
                  variant="secondary"
                  onClick={() => {
                    printLabels(currentPrinter, currentLabel, checkedOrders, twinTurboRoll);
                  }}
                >
                  Print adressen (DYMO)
                </Button>
              </div> */}

              <div className="confirmedOrderPrintOptions">
                <Button
                  disabled={!labelBase64List.length}
                  className="viewLabelsButton"
                  variant="outline-primary"
                  onClick={handleViewLabels}
                >
                  Bekijk Labels
                </Button>
                <Button
                  disabled={!labelBase64List.length}
                  className="viewLabelsButton"
                  variant="primary"
                  onClick={handlePrint}
                >
                  Print Labels
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {Array.isArray(checkedOrders) && checkedOrders.length > 0 && checkedOrders.map((order, index) => (
        <OrderListItemsBolShipStep3 order={order} key={order.id || index} index={index} />
      ))}

      <div className="orders-container-footer-actions ship-orders-container-footer-actions">
        <Button
          onClick={() => {
            navigate("/orders/bol/fbr/dbr/");
            window.scrollTo(0, 0);
          }}
        >
          Terug naar bestellingen
        </Button>

        <div style={{ marginLeft: "auto", display: "flex", gap: 12 }}>
          <Button
            disabled={!labelBase64List.length}
            className="viewLabelsButton"
            variant="outline-primary"
            onClick={handleViewLabels}
          >
            Bekijk Labels
          </Button>
          <Button
            disabled={!labelBase64List.length}
            className="viewLabelsButton"
            variant="primary"
            onClick={handlePrint}
          >
            Print Labels
          </Button>
        </div>
      </div>

      {loadingState && (
        <div className="loading-container">
          <div className="loading-container-title">{loadingState}</div>
          <RotateLoader size={100} width={100} height={10} color="#fff" loading />
        </div>
      )}

      {errorMessage && (
        <div className="orders-error-banner">
          {errorMessage}
          <Button size="sm" variant="link" onClick={() => setErrorMessage(null)}>
            Sluiten
          </Button>
        </div>
      )}

      <AssistantToggleButton />
    </React.Fragment>
  );
};

export default OrdersFbrDbr;
